import p5 from "p5";

export class App {
  private p?: any;
  private container: HTMLElement | null = null;

  initialize(): void {
    this.container = document.getElementById("p5-container");
    if (!this.container) {
      throw new Error("#p5-container が見つかりません");
    }
    const sketch = (s: any) => {
      // === 状態変数 ===
      type DotPoint = {
        x: number;
        y: number;
        x0: number;
        y0: number;
        vx: number;
        vy: number;
        size: number;
      };
      type TargetPoint = { x: number; y: number };

      let pts: DotPoint[] = [];
      let targets: TargetPoint[] = [];
      let cols = 60,
        rows = 60;
      let marginX = 60,
        marginY = 60;
      let spacingX = 0,
        spacingY = 0;

      const damping = 0.82;
      const springK = 0.06;
      let shapeMode = 0; // 0..5（内部用6）
      let textWord = "電光掲示板";

      // マウス回避
      const repelRadius = 120;
      const repelStrength = 1.2;
      const repelSigma = repelRadius * 0.85;

      // 自動切替
      let autoSwitch = true;
      let switchIntervalSec = 4;
      let lastSwitchMs = 0;
      const MODE_GRID = 0,
        MODE_CIRCLE = 1,
        MODE_HEART = 2,
        MODE_STAR = 3,
        MODE_SPIRAL = 4,
        MODE_TEXT = 5,
        MODE_MOFIX = 6;
      const modeSequence = [
        MODE_GRID,
        MODE_MOFIX,
        MODE_CIRCLE,
        MODE_HEART,
        MODE_STAR,
        MODE_SPIRAL,
        MODE_TEXT,
      ];
      let seqIndex = 0;

      s.setup = () => {
        const cnv = s.createCanvas(s.windowWidth, s.windowHeight);
        cnv.parent(this.container!);
        s.pixelDensity(1);
        initGrid();
        setShape(MODE_GRID);
        instantSnapToTargets();
        lastSwitchMs = s.millis();
        seqIndex = 0;
      };
      s.draw = () => {
        s.background(12);

        // 自動切替
        if (autoSwitch) {
          const now = s.millis();
          if (now - lastSwitchMs >= switchIntervalSec * 1000) {
            seqIndex = (seqIndex + 1) % modeSequence.length;
            setShape(modeSequence[seqIndex]);
            lastSwitchMs = now;
          }
        }

        // 物理更新（バネ + マウス回避）
        for (let i = 0; i < pts.length; i++) {
          const p = pts[i];
          const tg = targets[i] || { x: p.x0, y: p.y0 };
          let ax = (tg.x - p.x) * springK;
          let ay = (tg.y - p.y) * springK;

          if (mouseInCanvas()) {
            const dxm = p.x - s.mouseX;
            const dym = p.y - s.mouseY;
            const d2 = dxm * dxm + dym * dym;
            if (d2 > 0) {
              const d = Math.sqrt(d2);
              if (d < repelRadius) {
                const fall = Math.exp(-d2 / (2 * repelSigma * repelSigma));
                const ux = dxm / d;
                const uy = dym / d;
                ax += ux * repelStrength * fall;
                ay += uy * repelStrength * fall;
              }
            }
          }

          p.vx = (p.vx + ax) * damping;
          p.vy = (p.vy + ay) * damping;
          p.x += p.vx;
          p.y += p.vy;
          const sp = Math.hypot(p.vx, p.vy);
          p.size = s.lerp(p.size, 3 + s.constrain(sp * 1.2, 0, 6), 0.2);
        }

        // ドット描画
        s.noStroke();
        s.fill(220, 240, 255);
        for (const p of pts) s.circle(p.x, p.y, p.size);

        // drawHUD();
      };
      s.windowResized = () => {
        s.resizeCanvas(s.windowWidth, s.windowHeight);
        initGrid();
        setShape(shapeMode);
        lastSwitchMs = s.millis();
      };

      function initGrid() {
        pts = [];
        spacingX = (s.width - marginX * 2) / (cols - 1);
        spacingY = (s.height - marginY * 2) / (rows - 1);
        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            const px = marginX + x * spacingX;
            const py = marginY + y * spacingY;
            pts.push({ x: px, y: py, x0: px, y0: py, vx: 0, vy: 0, size: 3 });
          }
        }
      }

      function mouseInCanvas() {
        return s.mouseX >= 0 && s.mouseX <= s.width && s.mouseY >= 0 && s.mouseY <= s.height;
      }

      function setShape(mode: number) {
        shapeMode = mode;
        let shapePts: TargetPoint[] = [];
        const N = cols * rows;
        switch (mode) {
          case MODE_GRID:
            shapePts = shapeGrid(N);
            break;
          case MODE_CIRCLE:
            shapePts = shapeCircle(N);
            break;
          case MODE_HEART:
            shapePts = shapeHeart(N);
            break;
          case MODE_STAR:
            shapePts = shapeStar(N);
            break;
          case MODE_SPIRAL:
            shapePts = shapeSpiral(N);
            break;
          case MODE_TEXT:
            shapePts = shapeText(N, textWord);
            break;
          case MODE_MOFIX:
            shapePts = shapeText(N, "MOFING");
            break;
        }
        targets = remapToCount(shapePts, N);

        // 角度順に対応付け
        const cen = { x: s.width / 2, y: s.height / 2 };
        const srcOrder = pts
          .map((p, i) => ({ i, a: Math.atan2(p.y - cen.y, p.x - cen.x) }))
          .sort((a, b) => a.a - b.a)
          .map((o) => o.i);
        const tarOrder = targets
          .map((p, i) => ({ i, a: Math.atan2(p.y - cen.y, p.x - cen.x) }))
          .sort((a, b) => a.a - b.a)
          .map((o) => o.i);
        const newTargets: TargetPoint[] = new Array(N);
        for (let k = 0; k < N; k++) newTargets[srcOrder[k]] = targets[tarOrder[k]];
        targets = newTargets;

        const idx = modeSequence.indexOf(shapeMode);
        if (idx >= 0) seqIndex = idx;
      }

      function shapeGrid(_N: number): TargetPoint[] {
        const arr: TargetPoint[] = [];
        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            arr.push({ x: marginX + x * spacingX, y: marginY + y * spacingY });
          }
        }
        return arr;
      }

      function shapeCircle(N: number): TargetPoint[] {
        const r = Math.min(s.width, s.height) * 0.33;
        const cx = s.width / 2,
          cy = s.height / 2;
        const arr: TargetPoint[] = [];
        for (let i = 0; i < N; i++) {
          const a = s.TWO_PI * (i / N);
          arr.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
        }
        return jitterFill(arr, 0.015);
      }

      function shapeHeart(N: number): TargetPoint[] {
        const R = Math.min(s.width, s.height) * 0.33;
        const HEART_SCALE = 0.85;
        const scale = (R * HEART_SCALE) / 16.0;
        const cx = s.width / 2,
          cy = s.height * 0.52;
        const arr: TargetPoint[] = [];
        for (let i = 0; i < N; i++) {
          const t = s.TWO_PI * (i / N);
          const x = 16 * Math.pow(Math.sin(t), 3);
          const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
          arr.push({ x: cx + x * scale, y: cy - y * scale });
        }
        return jitterFill(arr, 0.012);
      }

      function shapeStar(N: number): TargetPoint[] {
        const R = Math.min(s.width, s.height) * 0.34;
        const r = R * 0.45;
        const cx = s.width / 2,
          cy = s.height / 2;
        const outline: TargetPoint[] = [];
        for (let i = 0; i < 10; i++) {
          const ang = -s.HALF_PI + (i * s.TWO_PI) / 10;
          const rr = i % 2 === 0 ? R : r;
          outline.push({ x: cx + Math.cos(ang) * rr, y: cy + Math.sin(ang) * rr });
        }
        const OUTLINE_RATIO = 0.85;
        const INSIDE_RATIO = 0.15;
        const arr = resamplePolyline(outline, s.int(N * OUTLINE_RATIO));
        const inside: TargetPoint[] = [];
        for (let i = 0; i < s.int(N * INSIDE_RATIO); i++) {
          const a = s.random(s.TWO_PI);
          const k = s.random(0.2, 1.0);
          const rr = s.lerp(r, R, k);
          inside.push({ x: cx + Math.cos(a) * rr * 0.7, y: cy + Math.sin(a) * rr * 0.7 });
        }
        return remapToCount(arr.concat(inside), N);
      }

      function shapeSpiral(N: number): TargetPoint[] {
        const cx = s.width / 2,
          cy = s.height / 2;
        const turns = 3.4;
        const maxR = Math.min(s.width, s.height) * 0.38;
        const arr: TargetPoint[] = [];
        for (let i = 0; i < N; i++) {
          const t = i / (N - 1);
          const ang = t * s.TWO_PI * turns - s.HALF_PI;
          const r = t * maxR;
          arr.push({ x: cx + Math.cos(ang) * r, y: cy + Math.sin(ang) * r });
        }
        return arr;
      }

      function shapeText(N: number, word: string): TargetPoint[] {
        // オフスクリーンバッファをキャンバス全体に
        const pg = s.createGraphics(s.width, s.height);
        pg.pixelDensity(1);
        pg.background(0);
        pg.fill(255);
        pg.noStroke();
        pg.textAlign(pg.CENTER, pg.CENTER);

        // 文字サイズを調整（小さめに）
        const baseSize = Math.min(pg.width * 0.1, pg.height * 0.4);
        pg.textSize(baseSize);
        const targetW = pg.width * 0.85; // さらにマージンを確保
        const targetH = pg.height * 0.8; // さらにマージンを確保
        const textW = Math.max(1, pg.textWidth(word));
        const textH = Math.max(1, pg.textAscent() + pg.textDescent());
        const fitScale = Math.min(targetW / textW, targetH / textH);
        const finalSize = Math.max(8, baseSize * fitScale);
        pg.textSize(finalSize);

        // バウンディングボックスを計測して精密に中央揃え
        s.push();
        s.textSize(finalSize);
        const bounds = s.textBounds(word.toUpperCase(), 0, 0);
        s.pop();
        const textDrawX = pg.width / 2 - (bounds.x + bounds.w / 2);
        const textDrawY = pg.height / 2 - (bounds.y + bounds.h / 2);
        pg.text(word.toUpperCase(), textDrawX, textDrawY);

        pg.loadPixels();
        const arr: TargetPoint[] = [];
        const step = Math.max(2, Math.floor((pg.width * pg.height) / (N * 5)));
        const stride = s.int(Math.sqrt(step));
        for (let y = 0; y < pg.height; y += stride) {
          for (let x = 0; x < pg.width; x += stride) {
            const idx = 4 * (y * pg.width + x);
            const bright = (pg.pixels as unknown as number[])[idx];
            if (bright > 128) {
              arr.push({ x: x, y: y });
            }
          }
        }
        return remapToCount(s.shuffle(arr, true), N);
      }

      // ------- helpers -------
      function remapToCount(arr: TargetPoint[], count: number): TargetPoint[] {
        const out: TargetPoint[] = [];
        if (arr.length === 0) {
          for (let i = 0; i < count; i++) out.push({ x: s.width / 2, y: s.height / 2 });
          return out;
        }
        if (arr.length >= count) {
          const step = arr.length / count;
          for (let i = 0; i < count; i++) out.push(arr[s.int(i * step)]);
        } else {
          for (let i = 0; i < count; i++) out.push(arr[i % arr.length]);
        }
        return out;
      }

      function resamplePolyline(poly: TargetPoint[], n: number): TargetPoint[] {
        const pts2 = poly.slice();
        pts2.push(poly[0]);
        let L = 0;
        const seg: number[] = [0];
        for (let i = 0; i < pts2.length - 1; i++) {
          L += Math.hypot(pts2[i + 1].x - pts2[i].x, pts2[i + 1].y - pts2[i].y);
          seg.push(L);
        }
        const out: TargetPoint[] = [];
        for (let i = 0; i < n; i++) {
          const d = (i / (n - 1)) * L;
          let k = 0;
          while (seg[k + 1] < d) k++;
          const r = (d - seg[k]) / (seg[k + 1] - seg[k] + 1e-6);
          const a = pts2[k],
            b = pts2[k + 1];
          out.push({ x: s.lerp(a.x, b.x, r), y: s.lerp(a.y, b.y, r) });
        }
        return out;
      }

      function jitterFill(arr: TargetPoint[], ratio: number): TargetPoint[] {
        const N = arr.length;
        const extra = s.int(N * ratio);
        const out = arr.slice();
        for (let i = 0; i < extra; i++) {
          const base = arr[s.int(s.random(N))];
          out.push({ x: base.x + s.random(-6, 6), y: base.y + s.random(-6, 6) });
        }
        return out;
      }

      // ------- UI / Controls -------
      s.keyPressed = () => {
        if ((s.key === "R" || s.key === "r") && s.keyIsDown(s.SHIFT)) {
          instantSnapToTargets();
          lastSwitchMs = s.millis();
          return;
        }
        if (s.key === "R" || s.key === "r") {
          fullResetToGrid();
          lastSwitchMs = s.millis();
          return;
        }
        if (s.key === "A" || s.key === "a") {
          autoSwitch = !autoSwitch;
          lastSwitchMs = s.millis();
          return;
        }
        if (s.key === "+" || s.key === "=") {
          switchIntervalSec = s.constrain(switchIntervalSec + 1, 1, 60);
          lastSwitchMs = s.millis();
          return;
        }
        if (s.key === "-" || s.key === "_") {
          switchIntervalSec = s.constrain(switchIntervalSec - 1, 1, 60);
          lastSwitchMs = s.millis();
          return;
        }
        if (s.key === "T" || s.key === "t") {
          const w = prompt("テキストを入力（英数字推奨）", textWord);
          if (w && w.trim().length) {
            textWord = w.trim();
            setShape(MODE_TEXT);
            lastSwitchMs = s.millis();
          }
          return;
        }
        const n = Number.parseInt(s.key, 10);
        if (!Number.isNaN(n) && n >= 0 && n <= 5) {
          setShape(n);
          lastSwitchMs = s.millis();
        }
      };

      function instantSnapToTargets() {
        if (!targets || targets.length !== pts.length) return;
        for (let i = 0; i < pts.length; i++) {
          pts[i].x = targets[i].x;
          pts[i].y = targets[i].y;
          pts[i].vx = pts[i].vy = 0;
          pts[i].size = 3;
        }
      }

      function fullResetToGrid() {
        setShape(MODE_GRID);
        for (let i = 0; i < pts.length; i++) {
          const gx = marginX + (i % cols) * spacingX;
          const gy = marginY + Math.floor(i / cols) * spacingY;
          pts[i].x = gx;
          pts[i].y = gy;
          pts[i].vx = pts[i].vy = 0;
          pts[i].size = 3;
        }
        seqIndex = 0;
      }

      // function drawHUD() {
      //   s.noStroke();
      //   s.fill(255, 220);
      //   s.textSize(14);
      //   const names = ["Grid", "Circle", "Heart", "Star", "Spiral", "Text", "MOFING"] as const;
      //   const shownName = names[shapeMode] ?? "Unknown";
      //   const autoTxt = autoSwitch ? `ON ${switchIntervalSec}s` : "OFF";
      //   const label = `Mode ${shapeMode}: ${shownName}   [0-5]形状 / T:テキスト / R:Grid / Shift+R:Snap / A:Auto(${autoTxt})  +/-:${switchIntervalSec}s`;
      //   s.text(label, 14, 24);
      // }
    };
    this.p = new p5(sketch);
  }

  destroy(): void {
    if (this.p) {
      this.p.remove();
      this.p = undefined;
    }
  }
}
